import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Checkbox,
    Col,
    Descriptions,
    Divider,
    Form,
    Input,
    InputNumber,
    Progress,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    Typography,
    message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import useRequest from '../hooks/useRequest';
import {
    AI_SETTINGS_CHANGED_EVENT,
    type AiSettingsForm,
    type AiSettingsView,
    aiErrorKey,
    budgetUsedPercent,
    formatUsd,
    toSettingsPayload,
} from '../domain/aiAssistant';

const { Title, Paragraph, Text } = Typography;

/**
 * Configuration > Ask AI (administrators): switch the assistant on, store the provider key,
 * choose the accepted models and their prices, cap the monthly spend, and see this month's
 * usage. The stored key is never sent back - only its last four characters.
 */
const AiAssistantSettings = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const [form] = Form.useForm<AiSettingsForm>();
    const [view, setView] = useState<AiSettingsView | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [clearApiKey, setClearApiKey] = useState(false);

    const applyView = useCallback(
        (data: AiSettingsView) => {
            setView(data);
            setClearApiKey(false);
            form.setFieldsValue({ ...data.settings, apiKey: '' });
        },
        [form],
    );

    useEffect(() => {
        let cancelled = false;
        request<{ data: AiSettingsView }>({ path: '/ai-assistant/settings' })
            .then(response => {
                if (!cancelled) applyView(response.data);
            })
            .catch(() => {
                if (!cancelled) setLoadFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, [request, applyView]);

    const save = async (values: AiSettingsForm) => {
        setSaving(true);
        try {
            const response = await request<{ data: AiSettingsView }>({
                path: '/ai-assistant/settings',
                method: 'PUT',
                params: toSettingsPayload(values, clearApiKey),
            });
            applyView(response.data);
            message.success(t('aiAssistant.settings.saved'));
            window.dispatchEvent(new Event(AI_SETTINGS_CHANGED_EVENT));
        } catch (error) {
            message.error(t(aiErrorKey(error)));
        } finally {
            setSaving(false);
        }
    };

    const testConnection = async () => {
        setTesting(true);
        try {
            const response = await request<{ data: { models: string[] } }>({
                path: '/ai-assistant/settings/test',
                method: 'POST',
            });
            message.success(t('aiAssistant.settings.testOk', { models: response.data.models.join(', ') }));
        } catch (error) {
            message.error(t(aiErrorKey(error)));
        } finally {
            setTesting(false);
        }
    };

    if (loadFailed) {
        return <Alert type="error" showIcon message={t('aiAssistant.settings.loadFailed')} />;
    }
    if (view === null) {
        return <Spin />;
    }

    const usage = view.usage.monthToDate;
    const usedPercent = budgetUsedPercent(usage.costUsd, view.usage.budgetUsd);
    const labelOf = (index: number): string => view.catalog[index]?.label ?? '';

    return (
        <div data-testid="ai-assistant-settings">
            <Title level={4}>{t('aiAssistant.settings.title')}</Title>
            <Paragraph type="secondary">{t('aiAssistant.settings.description')}</Paragraph>

            {!view.encryptionAvailable && (
                <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('aiAssistant.settings.encryptionUnavailable')} />
            )}

            <Form form={form} layout="vertical" onFinish={save} requiredMark={false}>
                <Form.Item name="enabled" label={t('aiAssistant.settings.enabled')} valuePropName="checked">
                    <Switch data-testid="ai-settings-enabled" />
                </Form.Item>

                <Form.Item
                    name="apiKey"
                    label={t('aiAssistant.settings.apiKey')}
                    extra={t('aiAssistant.settings.apiKeyHelp')}
                >
                    <Input.Password
                        autoComplete="new-password"
                        data-testid="ai-settings-api-key"
                        placeholder={view.apiKey.masked ?? ''}
                        disabled={clearApiKey}
                    />
                </Form.Item>
                <Space direction="vertical" style={{ marginBottom: 16 }}>
                    {view.apiKey.readable && (
                        <Text type="secondary" data-testid="ai-settings-key-status">
                            {t('aiAssistant.settings.apiKeyStored', { masked: view.apiKey.masked })}
                        </Text>
                    )}
                    {view.apiKey.configured && !view.apiKey.readable && (
                        <Text type="warning">{t('aiAssistant.settings.apiKeyUnreadable')}</Text>
                    )}
                    {!view.apiKey.configured && <Text type="secondary">{t('aiAssistant.settings.apiKeyMissing')}</Text>}
                    {view.apiKey.configured && (
                        <Checkbox checked={clearApiKey} onChange={event => setClearApiKey(event.target.checked)}>
                            {t('aiAssistant.settings.clearApiKey')}
                        </Checkbox>
                    )}
                </Space>

                <Divider orientation="left">{t('aiAssistant.settings.models')}</Divider>
                <Row gutter={16} style={{ fontWeight: 600, marginBottom: 8 }}>
                    <Col span={8}>{t('aiAssistant.settings.model')}</Col>
                    <Col span={4}>{t('aiAssistant.settings.accepted')}</Col>
                    <Col span={6}>{t('aiAssistant.settings.inputPrice')}</Col>
                    <Col span={6}>{t('aiAssistant.settings.outputPrice')}</Col>
                </Row>
                <Form.List name="models">
                    {fields =>
                        fields.map(field => (
                            <Row gutter={16} key={field.key} align="middle">
                                <Col span={8}>
                                    <Form.Item name={[field.name, 'id']} hidden>
                                        <Input />
                                    </Form.Item>
                                    <Text>{labelOf(field.name)}</Text>
                                </Col>
                                <Col span={4}>
                                    <Form.Item name={[field.name, 'enabled']} valuePropName="checked">
                                        <Switch size="small" aria-label={`${labelOf(field.name)} ${t('aiAssistant.settings.accepted')}`} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name={[field.name, 'inputPerMillion']} rules={[{ required: true }]}>
                                        <InputNumber<number> min={0} max={1000} step={0.1} style={{ width: '100%' }} addonBefore="$" />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name={[field.name, 'outputPerMillion']} rules={[{ required: true }]}>
                                        <InputNumber<number> min={0} max={1000} step={0.1} style={{ width: '100%' }} addonBefore="$" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        ))
                    }
                </Form.List>

                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item name="defaultModel" label={t('aiAssistant.settings.defaultModel')}>
                            <Select options={view.catalog.map(entry => ({ value: entry.id, label: entry.label }))} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="monthlyBudgetUsd"
                            label={t('aiAssistant.settings.monthlyBudget')}
                            extra={t('aiAssistant.settings.monthlyBudgetHelp')}
                            rules={[{ required: true }]}
                        >
                            <InputNumber<number> min={0} max={100000} step={5} style={{ width: '100%' }} addonBefore="$" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="maxOutputTokens"
                            label={t('aiAssistant.settings.maxOutputTokens')}
                            rules={[{ required: true }]}
                        >
                            <InputNumber<number> min={1024} max={64000} step={1024} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Space>
                    <Button type="primary" htmlType="submit" loading={saving} data-testid="ai-settings-save">
                        {t('aiAssistant.settings.save')}
                    </Button>
                    <Button onClick={() => void testConnection()} loading={testing} disabled={!view.apiKey.readable}>
                        {t('aiAssistant.settings.test')}
                    </Button>
                </Space>
            </Form>

            <Divider orientation="left">{t('aiAssistant.settings.usageTitle')}</Divider>
            <Descriptions size="small" column={{ xs: 1, md: 3 }} data-testid="ai-settings-usage">
                <Descriptions.Item label={t('aiAssistant.settings.requests')}>{usage.requests}</Descriptions.Item>
                <Descriptions.Item label={t('aiAssistant.settings.tokens')}>
                    {usage.inputTokens.toLocaleString()} / {usage.outputTokens.toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label={t('aiAssistant.settings.spent')}>{formatUsd(usage.costUsd)}</Descriptions.Item>
            </Descriptions>
            {usedPercent === null ? (
                <Text type="secondary">{t('aiAssistant.settings.noBudget')}</Text>
            ) : (
                <Progress
                    percent={usedPercent}
                    status={usedPercent >= 100 ? 'exception' : 'normal'}
                    format={percent => t('aiAssistant.settings.budgetUsed', { percent })}
                />
            )}
        </div>
    );
};

export default AiAssistantSettings;
